import { reviewed, type DataSource, type ReviewStatus } from './energyCalorimetry';
import { checkNumericAnswer, formatSig, type AnswerCheck } from './gasLaws';
import {
  ORGANIC_FAMILIES,
  ORGANIC_TRANSFORMATIONS,
  buildOrganicMolecule,
  type OrganicFamily,
  type OrganicTransformation,
} from './organicChemistry';

export type SynthesisMode = 'pathway' | 'green';

/** Stable identity used by reviewed CurriculumToolBinding records. */
export const SYNTHESIS_DESIGN_TOOL_IDENTITY = {
  id: 'synthesis-design',
  toolKey: 'chemistry-synthesis-design',
  toolVersion: '1.0.0',
  lifecycle: 'live',
  computation: 'client-deterministic',
  evidencePolicy: 'none',
  masteryPolicy: 'not-assessed',
  curriculumPolicy: 'reviewed-binding-required',
} as const;

export const SYNTHESIS_MODES: readonly SynthesisMode[] = ['pathway', 'green'];

/** CT-MODE: both modes start at Year 11; a Year opens the pathway mode and the learner switches. */
export const SYNTHESIS_YEAR_MODES: readonly { mode: SynthesisMode; fromYear: number }[] = [
  { mode: 'pathway', fromYear: 11 },
];

export const SYNTHESIS_SOURCES: Record<
  'organic' | 'yields' | 'anastas' | 'trost' | 'industrial' | 'iupac',
  DataSource
> = {
  organic: {
    label: 'Airbotix Organic Builder reaction families',
    detail:
      'Every step uses a reaction family already published in the Organic Builder: hydration, partial hydrogenation, controlled and further oxidation, elimination, esterification and substitution.',
  },
  yields: {
    label: 'Airbotix practice step yields',
    detail:
      'Step yields are practice values chosen for this exercise. They are not measured literature yields for a particular laboratory or industrial method.',
  },
  anastas: {
    label: 'Anastas, P. T. and Warner, J. C. (1998) Green Chemistry: Theory and Practice',
    detail: 'The twelve principles of green chemistry.',
  },
  trost: {
    label: 'Trost, B. M. (1991) Science 254, 1471 — the atom economy concept',
    detail: 'Atom economy = mass of desired product ÷ total mass of reactants × 100%.',
  },
  industrial: {
    label: 'Standard green chemistry teaching examples of industrial routes',
    detail:
      'Route equations are the overall balanced equations taught for these well-known comparisons. Real plants add catalysts, recycling and separation steps.',
  },
  iupac: {
    label: 'IUPAC/CIAAW standard atomic weights',
    detail: 'Molar masses for atom economy are calculated from standard atomic weights.',
  },
};

/** Learner yield answers within 2% of the model accept rounding to two or three figures. */
export const YIELD_TOLERANCE = 0.02;
const PERCENT = 100;

// ---------------------------------------------------------------------------
// Pathway cases
// ---------------------------------------------------------------------------

export interface PathwayMolecule {
  family: OrganicFamily;
  carbonCount: number;
  position?: number;
  /** Reviewed IUPAC name shown to the learner. */
  name: string;
}

export interface PathwayStep {
  transformationId: string;
  product: PathwayMolecule;
  /** Practice step yield, percent. */
  yieldPercent: number;
  /** Second reactant a step needs, for example the acid in esterification. */
  coReactant?: string;
}

export interface PathwayCase {
  id: string;
  start: PathwayMolecule;
  steps: PathwayStep[];
  note: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

function molecule(
  family: OrganicFamily,
  carbonCount: number,
  name: string,
  position?: number,
): PathwayMolecule {
  return { family, carbonCount, name, ...(position ? { position } : {}) };
}

function step(
  transformationId: string,
  product: PathwayMolecule,
  yieldPercent: number,
  coReactant?: string,
): PathwayStep {
  return { transformationId, product, yieldPercent, ...(coReactant ? { coReactant } : {}) };
}

function pathway(entry: Omit<PathwayCase, 'sources' | 'reviewStatus'>): PathwayCase {
  return { ...entry, ...reviewed([SYNTHESIS_SOURCES.organic, SYNTHESIS_SOURCES.yields]) };
}

const ETHENE = molecule('alkene', 2, 'ethene', 1);
const ETHANOL = molecule('alcohol', 2, 'ethanol', 1);
const ETHANAL = molecule('aldehyde', 2, 'ethanal');
const ETHANOIC_ACID = molecule('carboxylic-acid', 2, 'ethanoic acid');

export const PATHWAY_CASES: PathwayCase[] = [
  pathway({
    id: 'ethene-to-ethanol',
    start: ETHENE,
    steps: [step('alkene-hydration', ETHANOL, 95)],
    note: 'One addition step: every atom of ethene and water ends up in ethanol.',
  }),
  pathway({
    id: 'ethyne-to-ethanol',
    start: molecule('alkyne', 2, 'ethyne', 1),
    steps: [step('alkyne-hydrogenation', ETHENE, 70), step('alkene-hydration', ETHANOL, 95)],
    note: 'Add only one H₂ across the triple bond; full hydrogenation would give ethane, a dead end in this reaction set.',
  }),
  pathway({
    id: 'ethene-to-ethanoic-acid',
    start: ETHENE,
    steps: [
      step('alkene-hydration', ETHANOL, 95),
      step('alcohol-oxidation-aldehyde', ETHANAL, 80),
      step('aldehyde-oxidation-acid', ETHANOIC_ACID, 85),
    ],
    note: 'The oxidation level rises with each [O] step: alcohol, then aldehyde, then carboxylic acid.',
  }),
  pathway({
    id: 'chloroethane-to-ethanal',
    start: molecule('haloalkane', 2, 'chloroethane', 1),
    steps: [step('haloalkane-substitution', ETHANOL, 85), step('alcohol-oxidation-aldehyde', ETHANAL, 75)],
    note: 'Distil the aldehyde off as it forms so it is not oxidised further to the acid.',
  }),
  pathway({
    id: 'chloropropane-to-propanoic-acid',
    start: molecule('haloalkane', 3, '1-chloropropane', 1),
    steps: [
      step('haloalkane-substitution', molecule('alcohol', 3, 'propan-1-ol', 1), 80),
      step('alcohol-oxidation-aldehyde', molecule('aldehyde', 3, 'propanal'), 75),
      step('aldehyde-oxidation-acid', molecule('carboxylic-acid', 3, 'propanoic acid'), 90),
    ],
    note: 'A primary alcohol (–OH on C1) is needed for the aldehyde and acid route.',
  }),
  pathway({
    id: 'butanol-to-butanoic-acid',
    start: molecule('alcohol', 4, 'butan-1-ol', 1),
    steps: [
      step('alcohol-oxidation-aldehyde', molecule('aldehyde', 4, 'butanal'), 80),
      step('aldehyde-oxidation-acid', molecule('carboxylic-acid', 4, 'butanoic acid'), 90),
    ],
    note: 'In the laboratory, heating under reflux with excess oxidant takes the alcohol straight through to the acid.',
  }),
  pathway({
    id: 'butene-to-butanol',
    start: molecule('alkene', 4, 'but-2-ene', 2),
    steps: [step('alkene-hydration', molecule('alcohol', 4, 'butan-2-ol', 2), 90)],
    note: 'But-2-ene is symmetrical, so water can add only one way: the product is butan-2-ol.',
  }),
  pathway({
    id: 'butyne-to-butanol',
    start: molecule('alkyne', 4, 'but-2-yne', 2),
    steps: [
      step('alkyne-hydrogenation', molecule('alkene', 4, 'but-2-ene', 2), 70),
      step('alkene-hydration', molecule('alcohol', 4, 'butan-2-ol', 2), 85),
    ],
    note: 'Butan-2-ol is a secondary alcohol. Oxidising it would give a ketone, not an aldehyde.',
  }),
  pathway({
    id: 'chloromethane-to-methyl-ethanoate',
    start: molecule('haloalkane', 1, 'chloromethane', 1),
    steps: [
      step('haloalkane-substitution', molecule('alcohol', 1, 'methanol', 1), 85),
      step('alcohol-esterification', molecule('ester', 2, 'methyl ethanoate'), 65, 'ethanoic acid'),
    ],
    note: 'Esterification is an equilibrium, so its yield is limited unless water is removed or a reactant is in excess.',
  }),
  pathway({
    id: 'ethyne-to-ethanoic-acid',
    start: molecule('alkyne', 2, 'ethyne', 1),
    steps: [
      step('alkyne-hydrogenation', ETHENE, 70),
      step('alkene-hydration', ETHANOL, 95),
      step('alcohol-oxidation-aldehyde', ETHANAL, 80),
      step('aldehyde-oxidation-acid', ETHANOIC_ACID, 85),
    ],
    note: 'Four steps: even good step yields multiply into a much smaller overall yield.',
  }),
  pathway({
    id: 'chlorobutane-to-butanal',
    start: molecule('haloalkane', 4, '1-chlorobutane', 1),
    steps: [
      step('haloalkane-substitution', molecule('alcohol', 4, 'butan-1-ol', 1), 80),
      step('alcohol-oxidation-aldehyde', molecule('aldehyde', 4, 'butanal'), 70),
    ],
    note: 'Controlled oxidation stops at the aldehyde only if the aldehyde is removed as it forms.',
  }),
  pathway({
    id: 'chloropropane-to-propene',
    start: molecule('haloalkane', 3, '1-chloropropane', 1),
    steps: [
      step('haloalkane-substitution', molecule('alcohol', 3, 'propan-1-ol', 1), 80),
      step('alcohol-elimination', molecule('alkene', 3, 'propene', 1), 75),
    ],
    note: 'Using only the published families, the chlorine is swapped for –OH first and water is then eliminated.',
  }),
];

export function findTransformation(id: string): OrganicTransformation | null {
  return ORGANIC_TRANSFORMATIONS.find((entry) => entry.id === id) ?? null;
}

export function familyName(family: OrganicFamily): string {
  return ORGANIC_FAMILIES.find((entry) => entry.id === family)?.name.toLowerCase() ?? family;
}

/** Family name with its indefinite article, e.g. "an alkene", "a carboxylic acid". */
export function aFamily(family: OrganicFamily): string {
  const name = familyName(family);
  return `${/^[aeiou]/.test(name) ? 'an' : 'a'} ${name}`;
}

/** Molecular formula from the Organic Builder, so both tools agree. */
export function pathwayFormula(item: PathwayMolecule): string {
  return buildOrganicMolecule({
    family: item.family,
    carbonCount: item.carbonCount,
    position: item.position,
  }).molecularFormula;
}

export function pathwayTarget(entry: PathwayCase): PathwayMolecule {
  return entry.steps[entry.steps.length - 1].product;
}

export function pathwayTitle(entry: PathwayCase): string {
  return `${entry.start.name} → ${pathwayTarget(entry).name}`;
}

/** Molecule held after `completedSteps` correct steps. */
export function moleculeAfter(entry: PathwayCase, completedSteps: number): PathwayMolecule {
  if (completedSteps <= 0) return entry.start;
  return entry.steps[Math.min(completedSteps, entry.steps.length) - 1].product;
}

export type StepVerdict =
  | 'advance'
  | 'wrong-family'
  | 'secondary-alcohol'
  | 'off-route'
  | 'complete'
  | 'invalid';

export interface StepCheck {
  verdict: StepVerdict;
  feedback: string;
}

const PRIMARY_POSITION = 1;

/**
 * Checks one learner step against the reviewed route. Only published reaction families are
 * accepted; a valid reaction that leads away from the target is rejected with its outcome.
 */
export function checkPathwayStep(
  entry: PathwayCase,
  completedSteps: number,
  transformationId: string,
): StepCheck {
  const transformation = findTransformation(transformationId);
  if (!transformation) {
    return {
      verdict: 'invalid',
      feedback: 'That reaction is not one of the reviewed Organic Builder reaction families.',
    };
  }
  if (!Number.isInteger(completedSteps) || completedSteps < 0) {
    return { verdict: 'invalid', feedback: 'The route state is not valid. Reset the route.' };
  }
  const target = pathwayTarget(entry);
  if (completedSteps >= entry.steps.length) {
    return { verdict: 'complete', feedback: `The route already reaches ${target.name}.` };
  }
  const current = moleculeAfter(entry, completedSteps);
  if (transformation.from !== current.family) {
    return {
      verdict: 'wrong-family',
      feedback: `${transformation.label} needs ${aFamily(transformation.from)} to start from. You have ${current.name}, which is ${aFamily(current.family)}.`,
    };
  }
  if (
    transformation.id === 'alcohol-oxidation-aldehyde' &&
    (current.position ?? PRIMARY_POSITION) !== PRIMARY_POSITION
  ) {
    return {
      verdict: 'secondary-alcohol',
      feedback: `${current.name} is a secondary alcohol (–OH on C${current.position}). Oxidation gives a ketone, not an aldehyde, and ketone routes are not in this reviewed set.`,
    };
  }
  const expected = entry.steps[completedSteps];
  if (transformation.id === expected.transformationId) {
    const withReactant = expected.coReactant ? ` with ${expected.coReactant}` : '';
    return {
      verdict: 'advance',
      feedback: `${transformation.label}${withReactant} (${transformation.reagent}) turns ${current.name} into ${expected.product.name}. ${transformation.note}`,
    };
  }

  return {
    verdict: 'off-route',
    feedback: `${transformation.label} would turn ${current.name} into ${aFamily(transformation.to)}. That does not move towards ${target.name}. Which reaction makes ${aFamily(expected.product.family)} from ${aFamily(current.family)}?`,
  };
}

// ---------------------------------------------------------------------------
// Overall yield
// ---------------------------------------------------------------------------

export function overallYieldPercent(entry: PathwayCase): number {
  return entry.steps.reduce((total, item) => total * (item.yieldPercent / PERCENT), 1) * PERCENT;
}

/** Yield after each step, percent, for the cumulative chart. */
export function cumulativeYields(entry: PathwayCase): number[] {
  const values: number[] = [];
  entry.steps.reduce((total, item) => {
    const next = total * (item.yieldPercent / PERCENT);
    values.push(next * PERCENT);
    return next;
  }, 1);
  return values;
}

export function yieldWorking(entry: PathwayCase): string {
  const factors = entry.steps.map((item) => (item.yieldPercent / PERCENT).toFixed(2)).join(' × ');
  return `${factors} × 100% = ${formatSig(overallYieldPercent(entry), 3)}%`;
}

export function checkOverallYield(entry: PathwayCase, answer: number): AnswerCheck {
  const yields = entry.steps.map((item) => item.yieldPercent);
  const expected = overallYieldPercent(entry);
  const average = yields.reduce((sum, value) => sum + value, 0) / yields.length;
  const lowest = Math.min(...yields);
  const lossesAdded = PERCENT - yields.reduce((sum, value) => sum + (PERCENT - value), 0);
  const working = yieldWorking(entry);
  const single = entry.steps.length === 1;
  return checkNumericAnswer(
    answer,
    expected,
    [
      {
        value: average,
        feedback: `That is the average step yield. Each step works on what the previous step produced, so multiply: ${working}.`,
      },
      {
        value: lowest,
        feedback: `That is only the weakest step. The other steps also lose product, so multiply every step yield: ${working}.`,
      },
      {
        value: lossesAdded,
        feedback: `Subtracting each loss from 100% treats every loss as a share of the starting material. Later losses are shares of a smaller amount, so multiply: ${working}.`,
      },
      {
        value: expected / PERCENT,
        feedback: 'You found the fraction. Multiply by 100 to give a percentage.',
      },
    ],
    single
      ? `Correct: ${formatSig(expected, 3)}%. With one step, the overall yield is that step’s yield.`
      : `Correct: ${working}. Each step works only on the product the step before it made.`,
    '%',
    YIELD_TOLERANCE,
  );
}

export const SYNTHESIS_MODEL_BOUNDARIES: string[] = [
  'Routes use only the reaction families published in the Organic Builder. Real syntheses have many more options, such as direct elimination from a haloalkane.',
  'Step yields are practice values, not measured yields. Real yields depend on conditions, purification and equilibrium position.',
  'Regiochemistry and stereochemistry are kept out of these routes: hydration is only used on ethene and symmetrical but-2-ene.',
  'Atom economy compares balanced equations only. It ignores solvents, catalysts, energy use and the yield actually obtained.',
  'This is a planning model, not a laboratory method or risk assessment. Many reagents here are flammable, toxic or corrosive.',
];
